import React, { useState } from 'react'
import { Box, Button, Drawer, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useNavigate } from 'react-router-dom'
import { J1LeftarrowD } from '@e3dc-react/icons'
import CheckIcon from 'src/Resources/Images/unchecked_check.svg'
import { useEffectShopAccess, useShopCart } from 'src/Libs/shop/hooks'
import ProductCheckoutOverview from './ProductCheckoutOverview'
import SystemConfigurationCheckoutOverview from './SystemConfigurationCheckoutOverview'
import { ShopMainSkeleton } from 'src/Libs/shop/components/Skeletons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { currencyFormatter, formatQntmSystemConfigurationFromCart, formatSystemConfigurationFromCart } from '../../utils/formatter'
import ShopCheckoutSideBarPriceOverview from './ShopCheckoutSideBarPriceOverview'
import { ProductGeneralType, ProgressBarSteps, SystemConfigureType } from '../../types'
import { SYSTEM_CONFIGURE_PROGRESS_PAGE_PARAM } from '../../consts'
import { basketQuantityCounter, getCartAppliedPromotions } from '../../utils'
import PromotionCheckoutOverview from './PromotionCheckoutOverview'
import { ConfirmCheckoutPromotionCode } from '../ConfirmCheckout'
import { addConfigurator } from 'src/State/ShopSystemConfiguration/ShopSystemConfigurationAction'
import { useDispatch } from 'react-redux'
import { useShopCartContext } from '../../context'
import { useShopCartUpdater } from '../../hooks/use-shop-cart-updater'
import { addConfigurator as addQntmConfigurator } from 'src/State/ShopQntmSystemConfiguration/ShopQntmSystemConfigurationAction'

export interface ShopCheckoutSideBarModalProps {
  open: boolean
  onClose: () => void
  place: number
}

const useStyles = makeStyles()(theme => ({
  drawer: {
    zIndex: 1400,
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(1),
    paddingBottom: 0,
    maxHeight: '100%',
  },
  backwardContainer: {
    backgroundColor: theme.palette.grey.grey07,
  },
  spaceBetween: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  productNumbers: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(1),
    borderRadius: '22px',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    backgroundColor: theme.palette.primary.light,
  },
  iconWrapper: {
    width: '38px',
    height: '38px',
    backgroundColor: theme.palette.primary.main,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '22px',
  },
  icon: {
    width: '24px',
    height: '24px',
  },
  productWrapper: {
    overflowY: 'auto',
    flex: 1,
    maxHeight: '52vh',
    [theme.breakpoints.down('sm')]: {
      maxHeight: '48vh',
    },
  },
  paperProp: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 0,
    margin: 0,
    padding: 0,
    minWidth: '500px',
    maxWidth: '500px',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      minWidth: '100%',
    },
  },
}))

/*
 * This Component Is A Drawer Mui Component that shows all the information on the Cart, Image Quantity and Prices Including taxes.
 */

const ShopCheckoutSideBar: React.FC<ShopCheckoutSideBarModalProps> = ({ open, place, onClose }): JSX.Element => {
  const { t } = useCustomTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { getCheckoutProducts } = useShopCart()
  const { updateProductToCart, deleteProductFromCart, isLoading: isCartLoading } = useShopCartUpdater()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { cartData, setCartData } = useShopCartContext()
  const { classes } = useStyles()
  const appliedPromotions = getCartAppliedPromotions(cartData)

  useEffectShopAccess(() => {
    if (!isCartLoading) {
      getCheckoutProducts()
        .then(result => setCartData(result))
        .catch(error => console.log(error))
        .finally(() => setIsLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCheckoutLeaving = (): void => {
    onClose()
    navigate('/shop/checkout-confirm')
  }

  const handleEditSystemConfigurator = (productId: string, configurationType: ProductGeneralType): void => {
    const selectedConfigurator = cartData?.lineItems.find(configurator => configurator.id === productId)
    const desiredShippingDate = cartData?.extensions?.e3dcDesiredShippingDate
    if (selectedConfigurator && desiredShippingDate) {
      if (configurationType === ProductGeneralType.SYSTEM_CONFIGURATOR) {
        const configuratorPayload = formatSystemConfigurationFromCart(desiredShippingDate, selectedConfigurator)
        dispatch(addConfigurator(configuratorPayload))
        navigate(
          `/shop/system-configure/${SystemConfigureType.NormalSystem}/${productId}?${SYSTEM_CONFIGURE_PROGRESS_PAGE_PARAM}=${ProgressBarSteps.MainComponent}`
        )
      }
      if (configurationType === ProductGeneralType.QNTM_SYSTEM_CONFIGURATOR) {
        const configuratorPayload = formatQntmSystemConfigurationFromCart(desiredShippingDate, selectedConfigurator)
        dispatch(addQntmConfigurator(configuratorPayload))
        navigate(
          `/shop/system-configure/${SystemConfigureType.QntmSystem}/${productId}?${SYSTEM_CONFIGURE_PROGRESS_PAGE_PARAM}=${ProgressBarSteps.MainComponent}`
        )
      }
      onClose()
    }
  }

  const priceNet = currencyFormatter(cartData?.price.positionPrice)
  const totalShippingPrice = (cartData?.deliveries || []).reduce((total, delivery) => total + (delivery.shippingCosts.totalPrice || 0), 0)
  const formattedTotalShippingPrice = currencyFormatter(totalShippingPrice)
  const totalPrice = currencyFormatter(cartData?.price.netPrice)
  const savingsPrice = Math.abs(cartData?.extensions?.savings.total ?? 0)
  const totalProducts = basketQuantityCounter(cartData?.lineItems)

  return (
    <Drawer
      anchor="right"
      open={true}
      onClose={() => onClose()}
      className={classes.drawer}
      PaperProps={{
        className: classes.paperProp,
      }}
    >
      <Box sx={{ height: '100%' }}>
        <Box className={classes.backwardContainer}>
          <Button
            startIcon={<J1LeftarrowD />}
            sx={{ width: '100%', padding: 4, display: 'flex', justifyContent: 'flex-start' }}
            variant="text"
            color="inherit"
            onClick={() => {
              onClose()
            }}
          >
            {t('shop.checkout-side-bar.continue-shopping')}
          </Button>
        </Box>
        {isLoading ? (
          <Box marginTop={3}>
            <ShopMainSkeleton shopSkeletonType="product" />
          </Box>
        ) : (
          <Box className={classes.container}>
            <Box className={classes.spaceBetween}>
              <Typography variant="h6" color="inherit">
                {t('shop.checkout-side-bar.shopping-cart')}
              </Typography>
              <Typography variant="h5" color="inherit">
                {`${totalProducts} ${t('shop.checkout-side-bar.product')}`}
              </Typography>
            </Box>
            <Box className={classes.productNumbers}>
              <Box className={classes.iconWrapper}>
                <img src={CheckIcon} alt="Check Icon" className={classes.icon} />
              </Box>
              <Typography variant="h5" color="primary" marginLeft={2}>
                {`${totalProducts} ${t('shop.checkout-side-bar.product-added-successfully')}`}
              </Typography>
            </Box>
            <Box className={classes.productWrapper}>
              <Box className={classes.productWrapper}>
                {cartData?.lineItems.map(product => {
                  if (product.type === ProductGeneralType.SYSTEM_CONFIGURATOR || product.type === ProductGeneralType.QNTM_SYSTEM_CONFIGURATOR) {
                    return (
                      <SystemConfigurationCheckoutOverview
                        key={product.id}
                        onUpdateSystemConfigurationCart={updateProductToCart}
                        onDeleteSystemConfigurationCart={deleteProductFromCart}
                        onEditSystemConfigurator={handleEditSystemConfigurator}
                        configurator={product}
                        isLoading={isCartLoading}
                      />
                    )
                  } else if (product.type === ProductGeneralType.PRODUCT) {
                    return (
                      <ProductCheckoutOverview
                        key={product.id}
                        onUpdateProductCart={updateProductToCart}
                        onDeleteProductCart={deleteProductFromCart}
                        configurator={product}
                        isLoading={isCartLoading}
                      />
                    )
                  } else if (product.type === ProductGeneralType.PROMOTION) {
                    return (
                      <PromotionCheckoutOverview
                        key={product.id}
                        onDeleteProductCart={deleteProductFromCart}
                        promotionData={product}
                        isLoading={isCartLoading}
                      />
                    )
                  }
                  return null
                })}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      <ConfirmCheckoutPromotionCode
        fullWidth
        showErrorMessage
        onPromotionCodeUpdate={cartData => setCartData(cartData)}
        appliedPromotions={appliedPromotions}
      />
      <ShopCheckoutSideBarPriceOverview
        formattedPriceNet={priceNet}
        formattedShippingCosts={formattedTotalShippingPrice}
        formattedTotalPrice={totalPrice}
        savingsPrice={savingsPrice}
        onLeavingCheckoutSideBar={handleCheckoutLeaving}
      />
    </Drawer>
  )
}

export default ShopCheckoutSideBar
